import {
    Address,
    rpc as SorobanRpc,
    scValToNative,
    TransactionBuilder,
} from '@stellar/stellar-sdk/no-axios';

/**
 * Soroban `getTransaction` expects a 32-byte hash as **64 lowercase hex chars** (no `0x`).
 * We store `0x…` in Firestore for parity with EVM — strip it here.
 */
export function normalizeStellarTxHashForRpc(txHash) {
    const t = String(txHash ?? '').trim();
    const hex = t.startsWith('0x') || t.startsWith('0X') ? t.slice(2) : t;
    if (!/^[a-fA-F0-9]{64}$/.test(hex)) {
        throw new Error(
            `Invalid Stellar transaction hash (expected 64 hex chars, with or without 0x): got length ${hex.length}`
        );
    }
    return hex.toLowerCase();
}

function proofBytesFromScValNative(v) {
    if (v instanceof Uint8Array) return v;
    if (v != null && typeof v === 'object' && ArrayBuffer.isView(v)) {
        return new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
    }
    return null;
}

/**
 * Read `transfer_confidential` proof + token from a successful Soroban tx (same payload shape as the EVM path).
 *
 * @param {{ rpcUrl: string, networkPassphrase: string, gatewayContractId: string, txHash: string }} params
 * @returns {Promise<{ tokenAddress: string, proofBytes: Uint8Array }>}
 */
export async function fetchStellarTransferDecryptMaterial({
    rpcUrl,
    networkPassphrase,
    gatewayContractId,
    txHash,
}) {
    const hashNormalized = normalizeStellarTxHashForRpc(txHash);
    const server = new SorobanRpc.Server(rpcUrl, { allowHttp: String(rpcUrl || '').startsWith('http://') });
    const res = await server.getTransaction(hashNormalized);

    if (res.status === SorobanRpc.Api.GetTransactionStatus.NOT_FOUND) {
        throw new Error('Transaction not found on Soroban RPC yet. Try again in a moment.');
    }
    if (res.status === SorobanRpc.Api.GetTransactionStatus.FAILED) {
        throw new Error('On-chain transaction failed; transfer proof is unavailable.');
    }
    if (res.status !== SorobanRpc.Api.GetTransactionStatus.SUCCESS) {
        throw new Error(`Unexpected Soroban transaction status: ${res.status}`);
    }

    const envelope = res.envelopeXdr;
    if (!envelope) {
        throw new Error('Missing transaction envelope in Soroban getTransaction response');
    }

    const built = TransactionBuilder.fromXDR(envelope, networkPassphrase);
    const expected = String(gatewayContractId || '').trim().toUpperCase();
    if (!expected) {
        throw new Error('Missing gateway contract id for Stellar chain config');
    }

    const operations = built.operations || [];
    for (let i = 0; i < operations.length; i += 1) {
        const op = operations[i];
        if (op.type !== 'invokeHostFunction') continue;

        const fn = op.func;
        if (!fn || fn.switch().name !== 'hostFunctionTypeInvokeContract') continue;

        const invokeArgs = fn.value();
        const contractId = Address.fromScAddress(invokeArgs.contractAddress()).toString().toUpperCase();
        if (contractId !== expected) continue;

        const wasmName = invokeArgs.functionName();
        const method =
            typeof wasmName === 'string'
                ? wasmName
                : typeof wasmName?.toString === 'function'
                  ? wasmName.toString('utf8')
                  : String(wasmName);
        if (method !== 'transfer_confidential') continue;

        const scArgs = invokeArgs.args() || [];
        if (scArgs.length < 5) {
            throw new Error('transfer_confidential: unexpected argument count in on-chain transaction');
        }

        const tokenAddress = scValToNative(scArgs[2]);
        const proofNative = scValToNative(scArgs[3]);
        const proofBytes = proofBytesFromScValNative(proofNative);

        if (!tokenAddress || typeof tokenAddress !== 'string') {
            throw new Error('Could not read token contract from Soroban transaction');
        }
        if (!proofBytes?.length) {
            throw new Error('Could not read transfer proof bytes from Soroban transaction');
        }

        return { tokenAddress, proofBytes };
    }

    throw new Error(
        'No matching transfer_confidential call for this gateway contract was found in the transaction.'
    );
}
